import { useBreakpoint } from "@reactive-resume/hooks";
import { useTheme } from "@reactive-resume/hooks";
import { SidebarSimple } from "@phosphor-icons/react";
import { Button, Panel, PanelGroup, PanelResizeHandle, Sheet, SheetContent } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Outlet } from "react-router-dom";
import type { ImperativePanelHandle } from "react-resizable-panels";

import { useResumeSaveFlush } from "@/client/hooks/use-resume-save-flush";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore } from "@/client/stores/resume";

import { BuilderHeader } from "./_components/header";
import { BuilderToolbar } from "./_components/toolbar";
import { LeftSidebar } from "./sidebars/left";
import { RightSidebar } from "./sidebars/right";

const OutletSlot = () => (
  <>
    <BuilderHeader />

    <div className="absolute inset-0">
      <Outlet />
    </div>

    <BuilderToolbar />
  </>
);

export const BuilderLayout = () => {
  const { isDesktop } = useBreakpoint();
  const { isDarkMode } = useTheme();

  // Mounted on the LAYOUT rather than the page, so it spans the whole time the student is in the
  // builder on either breakpoint — the desktop and mobile trees below are two different returns, and
  // a flush attached to only one of them would leave the other losing its last edit.
  useResumeSaveFlush();

  const currentTemplate = useResumeStore((state) => state.resume.data?.metadata?.template ?? "");
  const isMastersUnion = currentTemplate === "mastersunion";
  const leftPanelRef = useRef<ImperativePanelHandle | null>(null);
  const rightPanelRef = useRef<ImperativePanelHandle | null>(null);
  const collapseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isPanelToggling, setIsPanelToggling] = useState(false);

  const sheet = useBuilderStore((state) => state.sheet);

  const leftSetSize = useBuilderStore((state) => state.panel.left.setSize);
  const rightSetSize = useBuilderStore((state) => state.panel.right.setSize);

  const leftHandle = useBuilderStore((state) => state.panel.left.handle);
  const rightHandle = useBuilderStore((state) => state.panel.right.handle);

  const onOpenAutoFocus = (event: Event) => event.preventDefault();
  const startPanelToggle = () => {
    setIsPanelToggling(true);

    if (collapseTimeoutRef.current) clearTimeout(collapseTimeoutRef.current);
    collapseTimeoutRef.current = setTimeout(() => {
      setIsPanelToggling(false);
      collapseTimeoutRef.current = null;
    }, 250);
  };

  const onToggleLeftPanel = () => {
    if (!leftPanelRef.current) return;
    startPanelToggle();
    if (leftPanelRef.current.isCollapsed()) leftPanelRef.current.expand();
    else leftPanelRef.current.collapse();
  };
  const onToggleRightPanel = () => {
    if (!rightPanelRef.current) return;
    startPanelToggle();
    if (rightPanelRef.current.isCollapsed()) rightPanelRef.current.expand();
    else rightPanelRef.current.collapse();
  };

  if (isDesktop) {
    return (
      <div className="relative size-full overflow-hidden">
        {isMastersUnion && (
          <>
            <Button
              asChild
              size="icon"
              variant="ghost"
              className="absolute left-2 top-2 z-30 size-8 rounded-full"
            >
              <Link to="/dashboard">
                <img
                  src={isDarkMode ? "/icon/CoachLogoDark.svg" : "/icon/CoachLogoLight.svg"}
                  width={14}
                  height={14}
                  alt="Coach LMS"
                  className="rounded-sm"
                />
              </Link>
            </Button>

            <Button
              size="icon"
              variant="outline"
              className="absolute left-2 top-12 z-30  rounded-full size-8 shadow-sm"
              onClick={onToggleLeftPanel}
            >
              <SidebarSimple />
            </Button>

            <Button
              size="icon"
              variant="outline"
              className="absolute right-2 top-12 z-30  rounded-full size-8 shadow-sm"
              onClick={onToggleRightPanel}
            >
              <SidebarSimple className="-scale-x-100" />
            </Button>
          </>
        )}

        <PanelGroup direction="horizontal">
          <Panel
            ref={leftPanelRef}
            collapsible={isMastersUnion}
            collapsedSize={0}
            minSize={25}
            maxSize={45}
            defaultSize={isMastersUnion ? 20 : 30}
            onResize={leftSetSize}
            className={cn(
              "z-10 bg-background",
              isMastersUnion && "ml-6 pr-6",
              !leftHandle.isDragging && !isPanelToggling && "transition-[flex]",
            )}
          >
            <LeftSidebar />
          </Panel>
          <PanelResizeHandle
            isDragging={leftHandle.isDragging}
            isMastersUnion={isMastersUnion}
            onDragging={leftHandle.setDragging}
          />
          <Panel>
            <OutletSlot />
          </Panel>
          <PanelResizeHandle
            isDragging={rightHandle.isDragging}
            isMastersUnion={isMastersUnion}
            onDragging={rightHandle.setDragging}
          />
          <Panel
            ref={rightPanelRef}
            collapsible={isMastersUnion}
            collapsedSize={0}
            minSize={isMastersUnion ? 20 : 25}
            maxSize={45}
            defaultSize={isMastersUnion ? 20 : 30}
            onResize={rightSetSize}
            className={cn(
              "z-10 bg-background",
              isMastersUnion && "mr-10",
              !rightHandle.isDragging && !isPanelToggling && "transition-[flex]",
            )}
          >
            <RightSidebar />
          </Panel>
        </PanelGroup>
      </div>
    );
  }

  return (
    <div className="relative">
      <Sheet open={sheet.left.open} onOpenChange={sheet.left.setOpen}>
        <SheetContent
          side="left"
          showClose={false}
          onOpenAutoFocus={onOpenAutoFocus}
          className="top-16 p-0 sm:max-w-xl"
        >
          <LeftSidebar />
        </SheetContent>
      </Sheet>

      <OutletSlot />

      <Sheet open={sheet.right.open} onOpenChange={sheet.right.setOpen}>
        <SheetContent
          side="right"
          showClose={false}
          onOpenAutoFocus={onOpenAutoFocus}
          className="top-16 p-0 sm:max-w-xl"
        >
          <RightSidebar />
        </SheetContent>
      </Sheet>
    </div>
  );
};
